/**
 * AppLayout — the shared shell for both CCA and EIA.
 *
 * The chrome is the fixed AppBar (brand + primary nav, driven by the nav list in
 * config/navigation.js); the body is the current route's <Outlet>. One shell,
 * both variants — variant differences live inside AppBar/pages, never in a
 * forked layout.
 *
 * `children`, when given, replaces the Outlet. That is how the signed-out Home
 * page gets the same header and footer as the signed-in app: the route guard
 * renders it in place of the requested page, outside the route tree, so there
 * is no Outlet for it to fill.
 */
import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { AppBar } from '@components/common/AppBar';
import { Footer } from '@components/common/Footer';

import styles from './AppLayout.module.css';

/**
 * Shown in the CONTENT AREA only while a page's code first downloads — the app
 * bar and footer stay. Navigations normally keep the previous page on screen
 * instead (App.jsx, v7_startTransition), so this appears only for a page's
 * very first paint. The library's amd-sk shimmer, as in the table skeleton.
 */
function PageFallback() {
  return (
    <div className={styles.pageFallback} aria-busy="true" aria-label="Loading page">
      <span className={`amd-sk ${styles.fallbackTitle}`} />
      <span className="amd-sk" />
      <span className={`amd-sk ${styles.fallbackShort}`} />
    </div>
  );
}

export function AppLayout({ children }) {
  return (
    <div className={styles.shell}>
      <AppBar />
      <main className={styles.main}>
        <Suspense fallback={<PageFallback />}>{children ?? <Outlet />}</Suspense>
      </main>
      {/* Outside <main>, as legacy mounts it outside <v-main>: it belongs to
          the shell, not to any page, and spans the full width. */}
      <Footer />
    </div>
  );
}

export default AppLayout;
